import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import type { UploadSummaryDto } from '../types/billing';

export interface RecentUploadsProps {
  uploads: UploadSummaryDto[];
  isLoading?: boolean;
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  STAGED: 'info',
  DISPATCHED: 'primary',
  OA_DRAFT_CREATED: 'secondary',
  SUBMITTED: 'success',
  FAILED: 'error',
  NEEDS_REVIEW: 'warning',
  SKIPPED: 'default',
};

const RecentUploads: React.FC<RecentUploadsProps> = ({ uploads, isLoading = false }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Recent uploads
      </Typography>

      {isLoading && (
        <List disablePadding>
          {[0, 1, 2].map((i) => (
            <ListItem key={i} disablePadding divider>
              <Box sx={{ py: 1.5, px: 2, width: '100%' }}>
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      {!isLoading && uploads.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No uploads yet - drop a CSV above to get started.
        </Typography>
      )}

      {!isLoading && uploads.length > 0 && (
        <List disablePadding>
          {uploads.map((upload) => {
            const statusEntries = Object.entries(upload.statusCountsByCategory).filter(
              ([, count]) => count > 0,
            );
            const sizeLabel = formatFileSize(upload.fileSizeBytes);
            const isAllSkipped = upload.rowCount === 0 && upload.skippedCount > 0;
            const claimsLabel = isAllSkipped
              ? `0 new · ${upload.skippedCount} already staged`
              : upload.skippedCount > 0
                ? `${upload.rowCount} new · ${upload.skippedCount} already staged`
                : `${upload.rowCount} claims`;
            const secondaryText = `${claimsLabel} · ${sizeLabel} · uploaded ${formatRelativeTime(upload.createdAt)}`;

            return (
              <ListItem
                key={upload.id}
                disablePadding
                divider
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isAllSkipped && (
                      <Chip
                        label="DEDUPED"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                    {statusEntries.map(([category, count]) => (
                      <Chip
                        key={category}
                        label={`${category} ${count}`}
                        size="small"
                        color={STATUS_COLORS[category] ?? 'default'}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                }
              >
                <ListItemButton
                  component={Link}
                  to={`/claims?uploadId=${upload.id}`}
                  sx={{ pr: statusEntries.length > 0 ? 16 : 2 }}
                >
                  <ListItemText
                    primary={upload.filename}
                    secondary={secondaryText}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default RecentUploads;
